/**
 * The TypeScript mirror of `salary-model-definition.schema.json` (`fantasy-contests.md` §4.2).
 *
 * <b>Hand-written, and it has to be.</b> A salary model definition is an opaque document on the
 * wire — stored verbatim, read back byte for byte years later, never queried into — so the OpenAPI
 * document types it `JsonElement` and the generated client `unknown`. There is nothing to
 * generate. Exactly the arrangement `points/definition.ts` already lives under, and the same
 * obligation follows: the schema, this file and `salaryValidate.ts` move together.
 *
 * Two properties of the round trip are load-bearing, both inherited from the points designer:
 *
 * - **Every numeric field is `Blankable`.** A half-typed number is not a number, and binding an
 *   input straight to a numeric field turns "clear the box and type 15" into a moment where the
 *   value is NaN.
 * - **`toSalaryDocument` strips.** The schema closes `additionalProperties` on the document, on
 *   every input, on the mapping and on each band, so a key the editor keeps for its own
 *   convenience must not reach the server. What is not in the schema is not written.
 */

export const SALARY_SCHEMA_VERSION = 1;

/** A blank numeric cell, mid-edit. See the note on this file. */
export type Blankable = number | null;

/**
 * The closed set of §4.2 inputs.
 *
 * `rating` is in the *schema's* enum and refused by the validator on both sides, which is what
 * lets the refusal say "reserved for a later version" rather than "not one of the allowed
 * values" — a message indistinguishable from a typo. It is therefore deliberately absent from
 * what this designer can author: the editor offers the six that work.
 */
export const SALARY_SOURCES = [
  'cashCount',
  'leaderboardPoints',
  'totalEarnings',
  'eventsEntered',
  'bestFinish',
  'firstPlaces',
] as const;

export type SalarySource = (typeof SALARY_SOURCES)[number];

/** Reserved, and refused by the server. Named so `salaryValidate` can say why. */
export const RESERVED_SALARY_SOURCE = 'rating';

export type SalaryRoundingMode = 'nearest' | 'up' | 'down';

/**
 * Which history a model prices from (§4.2's `lookback`).
 *
 * Resolved by the caller into concrete results before the engine runs — the engine never reads
 * this block, exactly as `PointsEngine` takes a scope as an argument rather than finding one.
 * `leaderboardIds` names boards outright; empty with `seriesCount` set means "the most recent N
 * closed leaderboards in this room".
 */
export interface SalaryLookback {
  mode: 'priorLeaderboards';
  leaderboardIds: string[];
  seriesCount: Blankable;
}

export interface SalaryInput {
  id: string;
  source: SalarySource;
  weight: Blankable;
}

export interface SalaryBand {
  fromPercentile: Blankable;
  toPercentile: Blankable;
  salary: Blankable;
}

export interface SalaryQualification {
  /** Null is "no minimum", which is a real answer and not a blank field. */
  minCashes: Blankable;
  minEvents: Blankable;
  maxPoolSize: Blankable;
}

export interface SalaryUnpriced {
  salary: Blankable;
  flagAsUnproven: boolean;
}

export interface SalaryRounding {
  mode: SalaryRoundingMode;
  increment: Blankable;
}

export interface SalaryDefinition {
  schemaVersion: number;
  name: string;
  description: string | null;
  lookback: SalaryLookback;
  inputs: SalaryInput[];
  /** v1's only mapping. Discriminated in the schema so `linear` and `auctionPrices` (§4.4) fit. */
  mapping: { type: 'percentileBands'; bands: SalaryBand[] };
  qualification: SalaryQualification;
  unpriced: SalaryUnpriced;
  rounding: SalaryRounding;
}

/**
 * The designer's own state, stored in `SalaryModelVersion.PreviewScope`.
 *
 * Free-form on the server — it validates only that it is an object — and it carries exactly one
 * thing: which boards the operator was previewing against. It is the counterpart of a points
 * system's `previewScope` and holds the same guarantee: it never reaches the engine, it is not
 * copied by a new version or a clone, and **publishing does not freeze it**, because it cannot
 * move anybody's salaries.
 */
export interface SalaryPreviewScope {
  leaderboardIds: string[];
}

// ---------------------------------------------------------------- reading

export function readSalaryDefinition(raw: unknown): SalaryDefinition {
  const doc = isRecord(raw) ? raw : {};
  const lookback = isRecord(doc.lookback) ? doc.lookback : {};
  const mapping = isRecord(doc.mapping) ? doc.mapping : {};
  const qualification = isRecord(doc.qualification) ? doc.qualification : {};
  const unpriced = isRecord(doc.unpriced) ? doc.unpriced : {};
  const rounding = isRecord(doc.rounding) ? doc.rounding : {};

  return {
    schemaVersion: num(doc.schemaVersion) ?? SALARY_SCHEMA_VERSION,
    name: str(doc.name) ?? '',
    description: str(doc.description) ?? null,
    lookback: {
      mode: 'priorLeaderboards',
      leaderboardIds: strings(lookback.leaderboardIds),
      // The schema's own default. A document that omits it prices from the last closed series.
      seriesCount: num(lookback.seriesCount) ?? 1,
    },
    inputs: readInputs(doc.inputs),
    mapping: {
      type: 'percentileBands',
      bands: readBands(mapping.bands),
    },
    qualification: {
      minCashes: num(qualification.minCashes),
      minEvents: num(qualification.minEvents),
      maxPoolSize: num(qualification.maxPoolSize),
    },
    unpriced: {
      salary: num(unpriced.salary) ?? 3,
      flagAsUnproven: bool(unpriced.flagAsUnproven) ?? true,
    },
    rounding: {
      mode: oneOf<SalaryRoundingMode>(rounding.mode, ['nearest', 'up', 'down'], 'nearest'),
      increment: num(rounding.increment) ?? 1,
    },
  };
}

function readInputs(raw: unknown): SalaryInput[] {
  if (!Array.isArray(raw)) return [];

  return raw.flatMap((entry, i) => {
    if (!isRecord(entry)) return [];
    const source = str(entry.source) ?? '';

    return [
      {
        id: str(entry.id) ?? `input${i + 1}`,
        // A document naming the reserved source is readable and unsavable: the editor shows what
        // is stored, and `salaryValidate` refuses it with the sentence that explains why.
        source: (SALARY_SOURCES as readonly string[]).includes(source)
          ? (source as SalarySource)
          : (source as SalarySource),
        weight: num(entry.weight),
      },
    ];
  });
}

function readBands(raw: unknown): SalaryBand[] {
  if (!Array.isArray(raw)) return [];

  return raw.flatMap((entry) =>
    isRecord(entry)
      ? [
          {
            fromPercentile: num(entry.fromPercentile),
            toPercentile: num(entry.toPercentile),
            salary: num(entry.salary),
          },
        ]
      : [],
  );
}

export function readSalaryPreviewScope(raw: unknown): SalaryPreviewScope | null {
  if (!isRecord(raw)) return null;
  return { leaderboardIds: strings(raw.leaderboardIds) };
}

// ---------------------------------------------------------------- writing

/**
 * The document as the server will store it.
 *
 * Blanks are written as `null` where the schema allows null and are simply carried where it does
 * not — `salaryValidate` refuses those before a save is ever offered, so an incomplete document
 * cannot reach here through the UI. Nothing the editor keeps for its own convenience is written:
 * three of the schema's objects close `additionalProperties`.
 */
export function toSalaryDocument(definition: SalaryDefinition): Record<string, unknown> {
  return {
    schemaVersion: SALARY_SCHEMA_VERSION,
    name: definition.name.trim(),
    ...(definition.description === null || definition.description.trim() === ''
      ? {}
      : { description: definition.description.trim() }),
    lookback: {
      mode: 'priorLeaderboards',
      leaderboardIds: [...definition.lookback.leaderboardIds],
      // Named boards win over a count on the server, so a count is still written beside them —
      // it is what the document falls back to if the ids are ever cleared.
      seriesCount: definition.lookback.seriesCount,
    },
    inputs: definition.inputs.map((input) => ({
      id: input.id.trim(),
      source: input.source,
      weight: input.weight,
    })),
    mapping: {
      type: 'percentileBands',
      bands: definition.mapping.bands.map((band) => ({
        fromPercentile: band.fromPercentile,
        toPercentile: band.toPercentile,
        salary: band.salary,
      })),
    },
    qualification: {
      minCashes: definition.qualification.minCashes,
      minEvents: definition.qualification.minEvents,
      maxPoolSize: definition.qualification.maxPoolSize,
    },
    unpriced: {
      salary: definition.unpriced.salary,
      flagAsUnproven: definition.unpriced.flagAsUnproven,
    },
    rounding: {
      mode: definition.rounding.mode,
      increment: definition.rounding.increment,
    },
  };
}

export function toSalaryPreviewScopeDocument(scope: SalaryPreviewScope): Record<string, unknown> {
  return { leaderboardIds: [...scope.leaderboardIds] };
}

/** What "unchanged" means for the dirty check: the document, not the editor's state. */
export function salaryDefinitionKey(definition: SalaryDefinition): string {
  return JSON.stringify(toSalaryDocument(definition));
}

export function salaryPreviewScopeKey(scope: SalaryPreviewScope): string {
  return JSON.stringify(toSalaryPreviewScopeDocument(scope));
}

// ---------------------------------------------------------------- derived reading

/**
 * The weights as shares, which is what an operator is really setting.
 *
 * §4.2 normalises weights to sum to 1.0 at validation, so `60/25/15` and `0.60/0.25/0.15` are the
 * same document — which means the raw number in the box says nothing on its own. This is what the
 * editor prints beside it.
 */
export function weightShares(inputs: readonly SalaryInput[]): number[] {
  const total = inputs.reduce((sum, input) => sum + Math.max(input.weight ?? 0, 0), 0);
  if (total <= 0) return inputs.map(() => 0);
  return inputs.map((input) => (Math.max(input.weight ?? 0, 0) / total) * 100);
}

/**
 * Applies §4.2's rounding the way `SalaryEngine.Round` does, for the worked examples beside the
 * band table. `nearest` rounds halves **away from zero**, matching `PointsEngine.RoundForDisplay`
 * — two engines in one product must not disagree about what .5 does, and neither must the
 * preview of one.
 */
export function roundSalary(value: number, rounding: SalaryRounding): number {
  const step = rounding.increment;
  if (step === null || step <= 0 || !Number.isFinite(value)) return value;

  const units = value / step;

  const rounded =
    rounding.mode === 'up'
      ? Math.ceil(units)
      : rounding.mode === 'down'
        ? Math.floor(units)
        : // Away from zero on a tie, and computed on the absolute value so a negative would go
          // the same distance the other way. Salaries are positive, but the rule is the rule.
          Math.sign(units) * Math.round(Math.abs(units));

  // Multiplying back re-introduces binary error (0.1 * 3 = 0.30000000000000004), which would show
  // up in a worked example beside a whole number. Snapped to the increment's own precision.
  return Number((rounded * step).toFixed(decimalsOf(step)));
}

function decimalsOf(step: number): number {
  const text = String(step);
  const dot = text.indexOf('.');
  return dot === -1 ? 0 : Math.min(text.length - dot - 1, 6);
}

/**
 * The lowest price the model can produce — the "floor" §4.3 is about.
 *
 * Taken from the first band rather than the smallest, because §4.2's bands run bottom-up and the
 * bottom band is where the mass sits. A model whose bands are inverted is a warning rather than a
 * refusal, so the two can genuinely differ.
 */
export function floorBandSalary(definition: SalaryDefinition): number | null {
  const first = definition.mapping.bands[0];
  if (!first || first.salary === null) return null;
  return roundSalary(first.salary, definition.rounding);
}

/**
 * A short name for a new input, derived from what it reads.
 *
 * §4.2 requires an id, unique within the document, and it is reported in a per-player derivation.
 * It is also an audit-trail key rather than anything an operator has an opinion about, so the
 * builder generates one and never shows the field — a form asking a card room manager to invent
 * `boardPoints` is asking the wrong person a question. Two inputs may legitimately read one source
 * (their weights add), hence the suffix.
 */
export function nextInputId(source: SalarySource, existing: readonly SalaryInput[]): string {
  const taken = new Set(existing.map((input) => input.id.trim().toLowerCase()));
  if (!taken.has(source.toLowerCase())) return source;

  for (let n = 2; n < 100; n++) {
    const candidate = `${source}${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }

  return `${source}X`;
}

// ---------------------------------------------------------------- primitives

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function bool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}
